import { useCurrentPageAccess } from "@/app/router";
import { Breadcrumbs } from "@/shared/components/layout/Breadcrumbs";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { DataTable } from "@/shared/components/table";
import { Button } from "@/shared/components/ui/button";
import { toast } from "@/shared/hooks/useToast";
import { ExternalLink, Play, RotateCw } from "lucide-react";
import { useMemo, useState } from "react";
import { CollectionsE2EAssetDetailsDialog } from "../components/CollectionsE2EAssetDetailsDialog";
import { useCollectionsE2EColumns } from "../hooks/useCollectionsE2EColumns";
import { useE2ETestDashboardQuery, useRunE2ETestsMutation } from "../hooks/useE2ETest";
import type { CollectionsE2EAssetRow } from "../types/collection-types";

export function CollectionsE2EPage() {
    const columns = useCollectionsE2EColumns();
    const { canWriteCurrentPage } = useCurrentPageAccess();

    // 실제 데이터 연결
    const { data, isLoading, isError, refetch } = useE2ETestDashboardQuery();
    const runTestsMutation = useRunE2ETestsMutation();

    const [selectedAssets, setSelectedAssets] = useState<CollectionsE2EAssetRow[]>([]);

    // details modal
    const [detailOpen, setDetailOpen] = useState(false);
    const [currentAsset, setCurrentAsset] = useState<CollectionsE2EAssetRow | undefined>(undefined);

    const openDetails = (asset: CollectionsE2EAssetRow) => {
        setCurrentAsset(asset);
        setDetailOpen(true);
    };

    const canRunAny = selectedAssets.length > 0;

    const handleRefresh = async () => {
        await refetch();
        toast({ title: "새로고침 완료", description: "E2E 테스트 결과를 다시 불러왔습니다." });
    };

    const handleRunSelected = async () => {
        if (!canRunAny) {
            toast({ title: "재실행할 자산을 선택해주세요.", variant: "destructive" });
            return;
        }

        const assets = selectedAssets.map((asset) => ({
            dw_account_id: asset.dw_account_id,
            media_account_id: asset.media_account_id || "",
            can_run_test: asset.can_run_test,
        }));

        await runTestsMutation.mutateAsync({ assets });

        // 실행 후 선택 해제
        setSelectedAssets([]);
    };

    // 데이터 변환
    const assets = useMemo(() => data?.assets ?? [], [data]);
    const historyByDwAccountId = useMemo(() => data?.history_by_dw_account_id ?? {}, [data]);

    return (
        <div className="space-y-4">
            <Breadcrumbs />
            <PageHeader
                title="E2E 테스트 대시보드"
                description="E2E 테스트 결과를 조회하고 재실행할 수 있습니다."
                actions={
                    <>
                        <Button variant="outline" onClick={handleRefresh} disabled={isLoading}>
                            <RotateCw className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
                            새로고침
                        </Button>

                        <Button
                            onClick={handleRunSelected}
                            disabled={!canRunAny || runTestsMutation.isPending || !canWriteCurrentPage}
                        >
                            <Play className="mr-2 h-4 w-4" />
                            선택된 자산 재실행
                        </Button>

                        <Button asChild variant="outline">
                            <a
                                href="https://xpert.pudam.me/media-accounts?workspace_id=WP3BBVVTLWKB"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <ExternalLink className="mr-2 h-4 w-4" />
                                E2E Workspace 바로가기
                            </a>
                        </Button>
                    </>
                }
            />

            <DataTable
                columns={columns}
                data={assets}
                totalCount={assets.length}
                page={1}
                pageSize={50}
                isLoading={isLoading}
                isError={isError}
                enableRowSelection
                getRowId={(row) => row.dw_account_id}
                onRowSelectionChange={setSelectedAssets}
                onRowClick={(row) => openDetails(row)}
                rowSelectionTrigger="checkbox"
            />

            <CollectionsE2EAssetDetailsDialog
                open={detailOpen}
                onOpenChange={setDetailOpen}
                asset={currentAsset}
                history={currentAsset ? historyByDwAccountId[currentAsset.dw_account_id] ?? [] : []}
            />
        </div>
    );
}
